import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  TrendingUp, DollarSign, Shield, Leaf, AlertTriangle,
  Loader2, Sparkles, ChevronDown, ChevronRight, BarChart3
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell
} from 'recharts';

function KPICard({ title, value, sub, icon: Icon, color }) {
  return (
    <Card className="bg-white border border-slate-200 rounded-sm shadow-none hover:border-slate-300 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{title}</p>
            <p className="font-mono text-xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
          </div>
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${color}`}>
            <Icon className="h-3.5 w-3.5 text-white stroke-[1.5px]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TradePnLRow({ trade, expanded, onToggle }) {
  const breakdown = Object.entries(trade.cost_breakdown || {});
  return (
    <>
      <tr
        className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer"
        onClick={onToggle}
        data-testid={`trade-row-${trade.trade_id}`}
      >
        <td className="px-4 py-2.5 text-xs">
          <div className="flex items-center gap-1.5">
            {expanded ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
            <span className="font-mono font-bold text-[#0A2463]">{trade.trade_id}</span>
          </div>
        </td>
        <td className="px-4 py-2.5 text-xs text-slate-600">{trade.vessel_name || '—'}</td>
        <td className="px-4 py-2.5 text-xs text-slate-600">
          {trade.port_load && trade.port_discharge ? `${trade.port_load} → ${trade.port_discharge}` : '—'}
        </td>
        <td className="px-4 py-2.5 text-xs font-mono text-right text-slate-600">
          {trade.total_cargo_mt ? `${trade.total_cargo_mt.toLocaleString()} MT` : '—'}
        </td>
        <td className="px-4 py-2.5 text-xs font-mono text-right text-slate-900 font-bold">
          ${trade.total_costs.toLocaleString()}
        </td>
        <td className="px-4 py-2.5 text-xs font-mono text-right text-amber-600">
          ${trade.hidden_costs.toLocaleString()}
        </td>
        <td className="px-4 py-2.5 text-xs font-mono text-right text-green-700">
          ${trade.carbon_cost.toLocaleString()}
        </td>
        <td className="px-4 py-2.5 text-xs font-mono text-right font-bold text-slate-900">
          ${trade.true_total_cost.toLocaleString()}
        </td>
        <td className="px-4 py-2.5 text-xs font-mono text-right text-slate-500">
          ${trade.cost_per_mmbtu.toFixed(4)}
        </td>
      </tr>
      {expanded && breakdown.length > 0 && (
        <tr className="bg-slate-50/50">
          <td colSpan={9} className="px-8 py-3">
            <div className="flex flex-wrap gap-3">
              {breakdown.map(([type, amount]) => (
                <div key={type} className="px-3 py-1.5 bg-white border border-slate-100 rounded-sm">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">{type.replace(/_/g, ' ')}</p>
                  <p className="text-xs font-mono font-bold text-slate-800">${amount.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function ExecutiveDashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [trades, setTrades] = useState([]);
  const [briefings, setBriefings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingBriefing, setGeneratingBriefing] = useState(false);
  const [latestBriefing, setLatestBriefing] = useState(null);
  const [expandedTrade, setExpandedTrade] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [k, t, b] = await Promise.all([
        api.get('/executive/kpis'),
        api.get('/executive/trade-pnl'),
        api.get('/executive/briefings'),
      ]);
      setKpis(k.data);
      setTrades(t.data);
      setBriefings(b.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleGenerateBriefing = async () => {
    setGeneratingBriefing(true);
    try {
      const { data } = await api.post('/executive/briefing/generate');
      setLatestBriefing(data);
      const { data: b } = await api.get('/executive/briefings');
      setBriefings(b);
    } catch (e) { console.error(e); }
    finally { setGeneratingBriefing(false); }
  };

  const topCostTrades = trades.slice(0, 10).map(t => ({
    name: t.trade_id?.length > 12 ? t.trade_id.substring(0, 12) : t.trade_id,
    total: t.true_total_cost,
    hidden: t.hidden_costs,
    carbon: t.carbon_cost,
  }));

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="executive-loading">
        <Skeleton className="h-8 w-56" />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="executive-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Executive Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Trade P&L intelligence, AI briefings & portfolio analytics</p>
        </div>
        <Button
          onClick={handleGenerateBriefing}
          disabled={generatingBriefing}
          data-testid="generate-briefing-button"
          className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white text-xs"
        >
          {generatingBriefing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
          Generate AI Briefing
        </Button>
      </div>

      {/* 5 KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="executive-kpis">
        <KPICard
          title="Trade Count"
          value={kpis?.true_trade_margin?.trade_count || 0}
          sub={`$${(kpis?.true_trade_margin?.total_attributed_costs || 0).toLocaleString()} attributed`}
          icon={TrendingUp} color="bg-[#0A2463]"
        />
        <KPICard
          title="Hidden Costs Found"
          value={`$${(kpis?.hidden_costs_recovered?.total || 0).toLocaleString()}`}
          sub={`${kpis?.hidden_costs_recovered?.invoice_discrepancies || 0} invoices`}
          icon={AlertTriangle} color="bg-[#F59E0B]"
        />
        <KPICard
          title="Automation Rate"
          value={`${kpis?.automation_rate?.rate_pct || 0}%`}
          sub={`${kpis?.automation_rate?.auto_processed || 0} of ${kpis?.automation_rate?.total_invoices || 0}`}
          icon={BarChart3} color="bg-[#16A34A]"
        />
        <KPICard
          title="Carbon $/MMBtu"
          value={`$${(kpis?.carbon_cost_per_molecule?.avg_per_mmbtu || 0).toFixed(4)}`}
          sub="EU ETS average"
          icon={Leaf} color="bg-[#007AFF]"
        />
        <KPICard
          title="Demurrage Forecast"
          value={`$${(kpis?.annual_demurrage_forecast?.projected_total || 0).toLocaleString()}`}
          sub="Annual projection"
          icon={DollarSign} color="bg-[#E11D48]"
        />
      </div>

      {/* AI Briefing Banner */}
      {(latestBriefing || briefings[0]) && (
        <Card className="bg-[#0A2463]/[0.03] border border-[#0A2463]/20 rounded-sm shadow-none" data-testid="ai-briefing-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-sm bg-[#0A2463]/10 flex items-center justify-center shrink-0 mt-0.5">
                <Sparkles className="h-4 w-4 text-[#0A2463]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-bold text-[#0A2463]">AI Executive Briefing</p>
                  <Badge variant="outline" className="rounded-sm text-[9px] border-[#0A2463]/30 text-[#0A2463]">
                    {latestBriefing?.period || (briefings[0]?.period_start && briefings[0]?.period_end
                      ? `${new Date(briefings[0].period_start).toLocaleDateString()} - ${new Date(briefings[0].period_end).toLocaleDateString()}`
                      : 'Latest')}
                  </Badge>
                </div>
                <div className="text-xs text-slate-700 mt-1.5 leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {latestBriefing?.summary || briefings[0]?.summary_text || 'No briefing generated yet'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="pnl">
        <TabsList className="rounded-sm h-9 bg-slate-100 p-0.5">
          <TabsTrigger value="pnl" className="rounded-sm text-xs flex-1" data-testid="tab-trade-pnl">
            <DollarSign className="h-3 w-3 mr-1" />Trade P&L ({trades.length})
          </TabsTrigger>
          <TabsTrigger value="chart" className="rounded-sm text-xs flex-1" data-testid="tab-trade-chart">
            <BarChart3 className="h-3 w-3 mr-1" />Cost Breakdown
          </TabsTrigger>
          <TabsTrigger value="briefings" className="rounded-sm text-xs flex-1" data-testid="tab-briefings">
            <Sparkles className="h-3 w-3 mr-1" />Briefings ({briefings.length})
          </TabsTrigger>
        </TabsList>

        {/* Trade P&L Table */}
        <TabsContent value="pnl">
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardContent className="p-0">
              {trades.length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-400">No trade data available</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="trade-pnl-table">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2">Trade ID</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2">Vessel</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2">Route</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2">Cargo</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2">Apparent</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2">Hidden</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2">Carbon</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2">True Total</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2">$/MMBtu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trades.map(t => (
                        <TradePnLRow
                          key={t.trade_id}
                          trade={t}
                          expanded={expandedTrade === t.trade_id}
                          onToggle={() => setExpandedTrade(expandedTrade === t.trade_id ? null : t.trade_id)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Chart */}
        <TabsContent value="chart">
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800">True Cost by Trade</CardTitle>
            </CardHeader>
            <CardContent>
              {topCostTrades.length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-400">No cost data</div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topCostTrades} margin={{ left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#64748b' }} angle={-20} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                      <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={v => `$${v.toLocaleString()}`} />
                      <Bar dataKey="total" name="Total" fill="#0A2463" stackId="a" radius={[0, 0, 0, 0]} barSize={20} />
                      <Bar dataKey="hidden" name="Hidden" fill="#F59E0B" stackId="b" radius={[0, 0, 0, 0]} barSize={20} />
                      <Bar dataKey="carbon" name="Carbon" fill="#16A34A" stackId="b" radius={[2, 2, 0, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Briefings History */}
        <TabsContent value="briefings">
          <div className="space-y-3">
            {briefings.length === 0 ? (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardContent className="py-16 text-center">
                  <Sparkles className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
                  <p className="mt-4 text-sm text-slate-400">No briefings generated yet</p>
                  <p className="text-xs text-slate-300 mt-1">Click "Generate AI Briefing" to create one</p>
                </CardContent>
              </Card>
            ) : (
              briefings.map(b => (
                <Card key={b.id} className="bg-white border border-slate-200 rounded-sm shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="rounded-sm text-[9px]">
                        {b.period_start && b.period_end
                          ? `${new Date(b.period_start).toLocaleDateString()} - ${new Date(b.period_end).toLocaleDateString()}`
                          : b.briefing_type}
                      </Badge>
                      <span className="text-[10px] font-mono text-slate-400">
                        {b.generated_at ? new Date(b.generated_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{b.summary_text}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
